// pages/AdminDashboard.jsx
//
// Three management sections: Site Settings, Banners, Products.
// Banner Management is new - admin can add/edit/delete banners, upload
// a banner image, set title + description, and toggle a banner
// active/inactive. Homepage (BannerDisplay.jsx) shows only active ones.

import React, { useState, useEffect } from 'react';
import ImageUploader from '../components/ImageUploader';

import { API_BASE } from '../config';

function AdminDashboard() {
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [banners, setBanners] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingBanner, setEditingBanner] = useState(null);
  const [message, setMessage] = useState('');

  const token = localStorage.getItem('token');
  const authHeader = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    fetchProducts();
    fetchSettings();
    fetchBanners();
  }, []);

  const fetchProducts = async () => {
    const res = await fetch(`${API_BASE}/products`);
    setProducts(await res.json());
  };
  const fetchSettings = async () => {
    const res = await fetch(`${API_BASE}/settings`);
    setSettings(await res.json());
  };
  const fetchBanners = async () => {
    // /all so admin sees inactive banners too, not just what's live
    const res = await fetch(`${API_BASE}/banners/all`, { headers: authHeader });
    if (res.ok) setBanners(await res.json());
  };

  const handleProductSave = async (e) => {
    e.preventDefault();
    const isEditing = Boolean(editingProduct._id);
    const url = isEditing ? `${API_BASE}/products/${editingProduct._id}` : `${API_BASE}/products`;
    const method = isEditing ? 'PUT' : 'POST';
    const res = await fetch(url, { method, headers: authHeader, body: JSON.stringify(editingProduct) });
    if (res.ok) {
      setMessage('Product saved successfully!');
      setEditingProduct(null);
      fetchProducts();
    } else {
      setMessage('Failed to save product.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    const res = await fetch(`${API_BASE}/products/${id}`, { method: 'DELETE', headers: authHeader });
    if (res.ok) fetchProducts();
  };

  const handleSettingsSave = async (e) => {
    e.preventDefault();
    const res = await fetch(`${API_BASE}/settings`, { method: 'PUT', headers: authHeader, body: JSON.stringify(settings) });
    setMessage(res.ok ? 'Settings updated! Changes are now live on the website.' : 'Failed to update settings.');
  };

  // ---- BANNER HANDLERS ----

  const handleBannerSave = async (e) => {
    e.preventDefault();
    const isEditing = Boolean(editingBanner._id);
    const url = isEditing ? `${API_BASE}/banners/${editingBanner._id}` : `${API_BASE}/banners`;
    const method = isEditing ? 'PUT' : 'POST';
    const res = await fetch(url, { method, headers: authHeader, body: JSON.stringify(editingBanner) });
    if (res.ok) {
      setMessage('Banner saved successfully!');
      setEditingBanner(null);
      fetchBanners();
    } else {
      setMessage('Failed to save banner.');
    }
  };

  const handleBannerDelete = async (id) => {
    if (!window.confirm('Delete this banner?')) return;
    const res = await fetch(`${API_BASE}/banners/${id}`, { method: 'DELETE', headers: authHeader });
    if (res.ok) fetchBanners();
  };

  const handleBannerToggleActive = async (banner) => {
    const res = await fetch(`${API_BASE}/banners/${banner._id}`, {
      method: 'PUT',
      headers: authHeader,
      body: JSON.stringify({ active: !banner.active }),
    });
    if (res.ok) fetchBanners();
  };

  return (
    <div className="page">
      <h1 className="display" style={{ fontSize: '26px' }}>Admin Dashboard</h1>
      {message && <p style={{ color: '#3ea567', fontSize: '14px' }}>{message}</p>}

      <section className="panel" style={{ marginBottom: '30px' }}>
        <h2 style={{ marginTop: 0, fontSize: '18px' }}>Site Settings</h2>
        {settings && (
          <form onSubmit={handleSettingsSave}>
            <label className="field-label">Site Name</label>
            <input type="text" className="input" value={settings.siteName} onChange={(e) => setSettings({ ...settings, siteName: e.target.value })} style={{ marginBottom: '14px' }} />

            <ImageUploader currentUrl={settings.siteLogo} onUploaded={(url) => setSettings({ ...settings, siteLogo: url })} label="Site Logo" />
            <ImageUploader currentUrl={settings.qrCodeImage} onUploaded={(url) => setSettings({ ...settings, qrCodeImage: url })} label="Payment QR Code" />

            <label className="field-label">WhatsApp Number (country code, no + or spaces)</label>
            <input type="text" className="input" value={settings.whatsappNumber} onChange={(e) => setSettings({ ...settings, whatsappNumber: e.target.value })} style={{ marginBottom: '14px' }} />

            <label className="field-label">"NEW" Badge Duration (days)</label>
            <input type="number" min="1" className="input" value={settings.newBadgeWindowDays ?? 7} onChange={(e) => setSettings({ ...settings, newBadgeWindowDays: Number(e.target.value) })} style={{ marginBottom: '14px', maxWidth: '100px' }} />

            <button type="submit" className="btn-primary">Save Settings</button>
          </form>
        )}
      </section>

      {/* ---------- BANNER MANAGEMENT ---------- */}
      <section className="panel" style={{ marginBottom: '30px' }}>
        <h2 style={{ marginTop: 0, fontSize: '18px' }}>Homepage Banners</h2>
        <p className="muted" style={{ fontSize: '13px', marginTop: 0 }}>
          Active banners show at the top of the homepage automatically. Toggle a banner
          off to hide it without deleting it.
        </p>

        <button
          onClick={() => setEditingBanner({ imageUrl: '', title: '', description: '', active: true, order: 0 })}
          className="btn-primary"
          style={{ marginBottom: '16px' }}
        >
          + Add New Banner
        </button>

        {editingBanner && (
          <form onSubmit={handleBannerSave} className="panel" style={{ marginBottom: '20px', background: 'var(--bg)' }}>
            <ImageUploader
              currentUrl={editingBanner.imageUrl}
              onUploaded={(url) => setEditingBanner({ ...editingBanner, imageUrl: url })}
              label="Banner Image"
            />

            <label className="field-label">Title (optional)</label>
            <input
              type="text"
              className="input"
              value={editingBanner.title}
              onChange={(e) => setEditingBanner({ ...editingBanner, title: e.target.value })}
              placeholder="e.g. New Year Sale"
              style={{ marginBottom: '12px' }}
            />

            <label className="field-label">Description</label>
            <textarea
              className="input"
              value={editingBanner.description}
              onChange={(e) => setEditingBanner({ ...editingBanner, description: e.target.value })}
              placeholder="e.g. Flat 20% off on all yearly plans this week"
              style={{ marginBottom: '14px', minHeight: '60px' }}
            />

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px', fontSize: '13px' }}>
              <input
                type="checkbox"
                checked={editingBanner.active}
                onChange={(e) => setEditingBanner({ ...editingBanner, active: e.target.checked })}
              />
              Active (visible on homepage)
            </label>

            <label className="field-label">Display Order (lower shows first)</label>
            <input
              type="number"
              className="input"
              value={editingBanner.order}
              onChange={(e) => setEditingBanner({ ...editingBanner, order: Number(e.target.value) })}
              style={{ marginBottom: '14px', maxWidth: '100px' }}
            />

            <button type="submit" className="btn-primary" style={{ marginRight: '10px' }} disabled={!editingBanner.imageUrl}>
              Save Banner
            </button>
            <button type="button" onClick={() => setEditingBanner(null)} className="btn-outline">Cancel</button>
            {!editingBanner.imageUrl && (
              <p style={{ color: '#ff4757', fontSize: '12px', marginTop: '8px' }}>Upload a banner image before saving.</p>
            )}
          </form>
        )}

        {banners.length === 0 ? (
          <p className="muted" style={{ fontSize: '13px' }}>No banners yet. Add one above.</p>
        ) : (
          <table className="hud-table">
            <thead><tr><th>Image</th><th>Title</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {banners.map((b) => (
                <tr key={b._id}>
                  <td><img src={b.imageUrl} alt={b.title || 'Banner'} style={{ width: '70px', height: '40px', objectFit: 'cover', borderRadius: '4px' }} /></td>
                  <td>{b.title || <span className="muted">(no title)</span>}</td>
                  <td>
                    <span className={`badge-status ${b.active ? 'badge-delivered' : 'badge-cancelled'}`}>
                      {b.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <button onClick={() => handleBannerToggleActive(b)} className="btn-outline" style={{ padding: '5px 12px', fontSize: '12px', marginRight: '6px' }}>
                      {b.active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button onClick={() => setEditingBanner(b)} className="btn-outline" style={{ padding: '5px 12px', fontSize: '12px', marginRight: '6px' }}>Edit</button>
                    <button onClick={() => handleBannerDelete(b._id)} className="btn-outline" style={{ padding: '5px 12px', fontSize: '12px', color: '#ff4757' }}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="panel">
        <h2 style={{ marginTop: 0, fontSize: '18px' }}>Products</h2>
        <button
          onClick={() => setEditingProduct({ name: '', pricingTiers: [{ label: '1-month', price: 0 }], quantity: 0, icon: '', description: '', available: true })}
          className="btn-primary"
          style={{ marginBottom: '16px' }}
        >
          + Add New Product
        </button>

        {editingProduct && (
          <form onSubmit={handleProductSave} className="panel" style={{ marginBottom: '20px', background: 'var(--bg)' }}>
            <label className="field-label">Name</label>
            <input type="text" className="input" value={editingProduct.name} onChange={(e) => setEditingProduct({ ...editingProduct, name: e.target.value })} required style={{ marginBottom: '12px' }} />

            <label className="field-label">Pricing Tiers (validity + price)</label>
            {editingProduct.pricingTiers?.map((tier, index) => (
              <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                <input
                  type="text"
                  className="input"
                  placeholder="e.g. 1-month, 1-year, lifetime"
                  value={tier.label}
                  onChange={(e) => {
                    const updated = [...editingProduct.pricingTiers];
                    updated[index] = { ...updated[index], label: e.target.value };
                    setEditingProduct({ ...editingProduct, pricingTiers: updated });
                  }}
                  style={{ flex: 2 }}
                />
                <input
                  type="number"
                  className="input"
                  placeholder="Price"
                  value={tier.price}
                  onChange={(e) => {
                    const updated = [...editingProduct.pricingTiers];
                    updated[index] = { ...updated[index], price: Number(e.target.value) };
                    setEditingProduct({ ...editingProduct, pricingTiers: updated });
                  }}
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  onClick={() => {
                    const updated = editingProduct.pricingTiers.filter((_, i) => i !== index);
                    setEditingProduct({ ...editingProduct, pricingTiers: updated });
                  }}
                  className="btn-outline"
                  style={{ padding: '0 12px', color: '#ff4757' }}
                  disabled={editingProduct.pricingTiers.length <= 1}
                >
                  ×
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() =>
                setEditingProduct({
                  ...editingProduct,
                  pricingTiers: [...(editingProduct.pricingTiers || []), { label: '', price: 0 }],
                })
              }
              className="btn-outline"
              style={{ marginBottom: '14px', fontSize: '13px' }}
            >
              + Add Another Validity Option
            </button>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px', fontSize: '13px' }}>
              <input
                type="checkbox"
                checked={editingProduct.contactForPrice || false}
                onChange={(e) => setEditingProduct({ ...editingProduct, contactForPrice: e.target.checked })}
              />
              No fixed price - show "Contact for Price" instead (hides tiers above)
            </label>

            <label className="field-label">Stock Quantity</label>
            <input type="number" className="input" value={editingProduct.quantity} onChange={(e) => setEditingProduct({ ...editingProduct, quantity: Number(e.target.value) })} required style={{ marginBottom: '12px' }} />

            <ImageUploader currentUrl={editingProduct.icon} onUploaded={(url) => setEditingProduct({ ...editingProduct, icon: url })} label="Product Image" />

            <label className="field-label">Description (shown on the Checkout page only)</label>
            <textarea className="input" value={editingProduct.description} onChange={(e) => setEditingProduct({ ...editingProduct, description: e.target.value })} style={{ marginBottom: '14px', minHeight: '70px' }} />

            <button type="submit" className="btn-primary" style={{ marginRight: '10px' }}>Save Product</button>
            <button type="button" onClick={() => setEditingProduct(null)} className="btn-outline">Cancel</button>
          </form>
        )}

        <table className="hud-table">
          <thead><tr><th>Name</th><th>Price</th><th>Stock</th><th>Actions</th></tr></thead>
          <tbody>
            {products.map((p) => {
              const tiers = p.pricingTiers || [];
              const priceDisplay = p.contactForPrice
                ? 'Contact for Price'
                : tiers.length > 1
                ? `₹${Math.min(...tiers.map((t) => t.price))}–₹${Math.max(...tiers.map((t) => t.price))}`
                : `₹${p.price}`;
              return (
                <tr key={p._id}>
                  <td>{p.name}</td>
                  <td>{priceDisplay}</td>
                  <td>{p.quantity}</td>
                  <td>
                    <button onClick={() => setEditingProduct(p)} className="btn-outline" style={{ padding: '5px 12px', fontSize: '12px', marginRight: '6px' }}>Edit</button>
                    <button onClick={() => handleDelete(p._id)} className="btn-outline" style={{ padding: '5px 12px', fontSize: '12px', color: '#ff4757' }}>Delete</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
    </div>
  );
}

export default AdminDashboard;
